<template>
  <div class="options accordion">
    <input type="checkbox" id="accordion-for-options" name="accordion-checkbox" checked hidden />
    <label class="accordion-header c-hand text-primary" for="accordion-for-options">
      <i class="icon icon-arrow-right mr-1"></i>
      <strong>Filters & Options</strong>
    </label>
    <div class="accordion-body">
      <div class="form-horizontal columns">
        <div class="form-group column col-5">
          <div class="col-4 col-sm-12">
            <label class="form-label" for="fandom-filter">Filter </label>
          </div>
          <div class="col-8 col-sm-12">
            <input class="form-input" type="text" id="fandom-filter" placeholder="Search group by tag name..."
              v-model="options.filter.term" />
          </div>
        </div>

        <div class="form-group column col-5">
          <div class="col-4 col-sm-12">
            <label class="form-label" for="groupby-filter">Group By</label>
          </div>
          <div class="col-8 col-sm-12">
            <select id="groupby-filter" class="form-select" v-model="options.groupBy">
              <option v-for="gbo in groupByOptions" :key="gbo" :selected="gbo == options.groupBy">
                {{ gbo }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group column col-5" v-if="isFandomGroupBy">
          <div class="col-4 col-sm-12">
            <label class="form-label" for="category-filter">Category</label>
          </div>
          <div class="col-8 col-sm-12">
            <select id="category-filter" class="form-select" v-model="options.filter.category">
              <option value>All Categories</option>
              <option v-for="category in categories" :key="category">
                {{ category }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-checkbox form-inline" v-if="isFandomGroupBy || isFreeformGroupBy">
          <input type="checkbox" v-model="options.onlyPrompts" />
          <i class="form-icon"></i> Only {{ this.options.groupBy.toLowerCase() }}s with prompts
        </label>
        <label class="form-checkbox form-inline" v-if="isFandomGroupBy || isFreeformGroupBy || isRelationshipGroupBy">
          <input type="checkbox" v-model="options.onlyBookmarks" />
          <i class="form-icon"></i> Only bookmarked {{ this.options.groupBy.toLowerCase() }}s
        </label>


        <label class="form-checkbox form-inline" v-if="isFandomGroupBy">
          <input type="checkbox" id="load-all" v-model="options.loadAll" />
          <i class="form-icon"></i> Load everything!
          <span class="warn tooltip" data-tooltip="Load all fandom data on one page. 
This may be hard on your browser.">
            <sup>
              <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>
        <label class="form-checkbox form-inline" v-if="isFandomGroupBy">
          <input type="checkbox" v-model="options.hideCharacters" />
          <i class="form-icon"></i> Hide Characters column
        </label>

        <label class="form-checkbox form-inline">
          <input type="checkbox" v-model="options.destyle" />
          <i class="form-icon"></i> Mobile letter URLs
          <span class="tooltip c-hand" data-tooltip="Use the mobile/plain theme
for letters, if possible. Good if you want 
to avoid custom themes.">
            <sup>
              <i class="fa fa-question-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>

        <label class="form-checkbox form-inline">
          <input type="checkbox" v-model="options.useToggles" />
          <i class="form-icon"></i> Toggles for optional details
          <span class="tooltip c-hand" data-tooltip="Whether to use a toggle or display
the full text for optional details in prompts">
            <sup>
              <i class="fa fa-question-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>

        <label class="form-checkbox form-inline">
          <input type="checkbox" v-model="options.truncateTags" />
          <i class="form-icon"></i> Toggles for tags

          <span class="tooltip c-hand" data-tooltip="Limit visible 
freeform/optional tags unless toggled">
            <sup>
              <i class="fa fa-question-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>

        <label v-if="options.truncateTags" class="form-inline form-label mr-2" for="fandom-filter">after
          <input class="form-input form-inline max-tags" id="max-tags" type="number" placeholder="10" min="0"
            v-model="options.tagLimit">
          tags
        </label>



        <label class="form-checkbox form-inline">
          <input type="checkbox" v-model="options.openDetails" />
          <i class="form-icon"></i> Auto-expand details
          <span class="tooltip c-hand" data-tooltip="Opens HTML details elements">
            <sup>
              <i class="fa fa-question-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>

        <label class="form-checkbox form-inline">
          <input type="checkbox" v-model="options.charsAsTags" />
          <i class="form-icon"></i> Show characters/relationships as tags
        </label>

        <label class="form-checkbox form-inline" v-if="exchangeId.toLowerCase().indexOf('fic_in_a_box') > -1">
          <input type="checkbox" v-model="options.disableEasterEggs" />
          <i class="form-icon"></i> Disable easter eggs
          <span class="tooltip c-hand" data-tooltip="Turn off random visual easter eggs">
            <sup>
              <i class="fa fa-question-circle" aria-hidden="true"></i>
            </sup>
          </span>
        </label>

        <div class="mt-1">For field-level searching, use
          <router-link :to="{ path: `/${exchangeId}/all` }">Search All Requests</router-link>.
        </div>

        <!-- <span class="text-small" v-if="!isFandomGroupBy && !isFreeformGroupBy">Bookmarking for other group by options coming soon!</span> -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { debounce, includes } from "lodash-es";
import utils from "./utils"

export default {
  computed: {
    ...mapGetters(["categories", "loadAll", "exchangeId"]),
    isFandomGroupBy() {
      return this.options.groupBy.toLowerCase() == "fandom";
    },
    isFreeformGroupBy() {
      return this.options.groupBy.toLowerCase() == "freeform";
    },
    isRelationshipGroupBy() {
      return this.options.groupBy.toLowerCase() == "relationship";
    }
  },
  mounted() {
    this.options = this.$store.state.options;
    const queryparams = this.$route.query;
    if (queryparams && queryparams.groupby && includes(this.groupByOptions, queryparams.groupby)) {
      this.options.groupBy = queryparams.groupby;
    } else {
      this.options.groupBy = "Fandom";
    }
  },
  watch: {
    options: {
      handler(val) {
        if (val.filter.term.length) {
          debounce(() => {
            this.$store.commit("setOptions", val);
          }, 500)();
        } else {

          if (val.openDetails) {
            utils.openDetails();
          }

          this.$store.commit("setOptions", val);
          this.$localStorage.set("appOptions", JSON.stringify(val));
        }
      },
      deep: true
    },
  },
  data() {
    return {
      showMsg: false,
      groupByOptions: ["Fandom", "Character", "Freeform", "Relationship"],
      options: {
        filter: {
          category: "",
          term: ""
        },
        groupBy: "fandom",
        mediaType: "both",
        onlyBookmarks: false,
        onlyPrompts: true,
        destyle: false,
        loadAll: false,
        useToggles: false,
        disableEasterEggs: true,
        openDetails: false,
        charsAsTags: false,
        tableStyle: "split",
        hideCharacters: true,
        truncateTags: false,
        tagLimit: 10
      }
    };
  }
};
</script>

<style lang="scss" scoped>
.warn {
  color: #e4a95c;
  padding-left: 2px;
}

.max-tags {
  height: 1.2rem;
  line-height: 1.2rem;
  padding: 3px;
  width: 40px;
  margin-left: 0.25rem;
}
</style>
